import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAuth } from "../auth/AuthContext";
import { deliveriesApi, type Delivery } from "../api/deliveries";
import { dashboardApi, type ProcurementDashboard as ProcurementDashboardData } from "../api/dashboard";
import { flagEmoji } from "../i18n/countries";
import { formatMoney, formatNumber as brojLokalno } from "../lib/format";
import { fmtDate } from "./finance/money";
import {
  BarChart, Donut, HorizontalBars, KpiCard, LedgerTable, Muted, Section,
  actionLink, countryBars, kpiStrip, nazivMeseca, relTime, reviewItem, reviewList, reviewStatusChip,
} from "./dashboard/primitives";

/**
 * Kontrolna tabla otkupa, iz dokumenata koje portal drži.
 *
 * Ista tabla kao prodajna i namerno od istih delova: direktor komercijale čita svoje jutro onako
 * kako direktor prodaje čita svoje, pa iste pločice stoje na istim mestima, a razlika je samo u
 * tome koju stranu knjige čitaju — tamo šta kupci duguju Bankomu, ovde šta Bankom duguje
 * kooperantima.
 *
 * Tri stvari NISU ovde, i to je odluka a ne propust:
 *
 * Nema realizacije prema ugovorenom, jer bi joj imenilac bila ugovorena tonaža a
 * `procurement_contract` nema nijedan red — 692 predloga iz evidencije još čekaju mapiranje.
 * Nema novca po prijemu, jer `delivery.settled_amount` stoji prazno na svih 22.132 uvezena
 * kamiona: ERP obračunava, portal čita, i tu cifru nije dobio. I nema vaganja, jer bruto i tara
 * postoje kao kolone a vaga ih ne puni.
 *
 * Procenat prema izmišljenom imeniocu je jedini broj sa kontrolne table koji se citira na
 * sastanku, pa prazno mesto ovde vredi više od popunjenog.
 *
 * Broj prijema se čita po `received_on`, nikad po `announced_date`: istorija iz starog ERP-a
 * najavu nosi samo za manji deo kamiona (V56), pa bi brojanje najava prijavilo prazan mesec za
 * mesec koji je primio sto kamiona.
 */
export default function ProcurementDashboard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const { state, hasPermission } = useAuth();
  const canFinance = hasPermission("section:supplier_finance:read");
  const canIntake = hasPermission("section:delivery_intake:read");

  const [waiting, setWaiting] = useState<Delivery[] | null>(null);
  const [data, setData] = useState<ProcurementDashboardData | null>(null);

  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setData(null); setError(null);
    // Kontrolna tabla gleda šta čeka odluku, ne ceo registar prijema. Traži se mala strana sa
    // servera, a ne ceo spisak pa odsecanje u pregledaču.
    if (canIntake) {
      void Promise.all([
        deliveriesApi.list({ status: "ANNOUNCED" }, 0, 50),
        deliveriesApi.list({ status: "LAB_REJECTED" }, 0, 50),
      ])
        .then(([najave, odbijeni]) => {
          // Referent čita svakog kooperanta, ali red na ovoj listi je poziv na radnju, pa ostaju
          // samo oni na kojima ovaj referent sme da postupi.
          if (live) setWaiting([...najave.content, ...odbijeni.content].filter((d) => d.canAct));
        })
        .catch((e) => { if (live) { setWaiting([]); setError(e.message); } });
    } else setWaiting(null);
    if (canFinance || canIntake) void dashboardApi.procurement(12).then((result) => { if (live) setData(result); }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [canFinance, canIntake]);
  const showFinance = canFinance && !!data && data.financeVisible !== false;
  const showIntake = canIntake && !!data && data.intakeVisible !== false;

  const [period, setPeriod] = useState<3 | 6 | 12>(12);
  const [pipelineFilter, setPipelineFilter] = useState<string | null>(null);

  const novac = (n: number) => formatMoney(n, "EUR", locale, { maximumFractionDigits: 0 });
  // Each half of the board is counted up to the day its own data reaches, which can be earlier than
  // today: the invoices for the money, the intakes for the lorries. The tiles name the day.
  const otkupDo = data?.dataUntil
    ? t("dashboard.procurement.kpi.dataUntil", "podaci do {{datum}}", { datum: fmtDate(data.dataUntil) })
    : undefined;
  const nijeUracunato = data && ((data.carriedPostedEur ?? 0) > 0 || (data.otherPostedEur ?? 0) > 0)
    ? t("finance.notInvoiced", "Nije uračunato: preneto iz ranijih godina {{prenos}}, ostala knjiženja {{ostalo}}",
      { prenos: novac(data.carriedPostedEur ?? 0), ostalo: novac(data.otherPostedEur ?? 0) })
    : undefined;
  const mesecni = useMemo(
    () => (data?.monthlyPurchased ?? []).map((m) => ({ label: nazivMeseca(m.label, locale), value: m.value })),
    [data, locale],
  );
  const tokPoFazi = useMemo(
    () => (data?.intakePipeline ?? []).map((p) => ({
      label: statusNaziv(p.status, t), value: p.deliveries, color: statusBoja(p.status),
    })),
    [data, t],
  );

  if (state.status !== "authenticated") return null;
  const { user } = state;
  const hour = new Date().getHours();
  const timeGreetingKey =
    hour < 12 ? "dashboard.morning" : hour < 18 ? "dashboard.afternoon" : "dashboard.evening";
  const timeGreetingFallback =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const pipelineTotal = tokPoFazi.reduce((sum, p) => sum + p.value, 0);

  const decisionQueue = [...(waiting ?? [])]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 6);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("dashboard.breadcrumb.aria", "Breadcrumb")}>
            <span>{t("nav.home", "Home")}</span>
            <span>{t("nav.dashboard", "Dashboard")}</span>
          </nav>
          <h1>{t(timeGreetingKey, { defaultValue: timeGreetingFallback })}, {user.firstName}</h1>
          <div className="sub">{t("dashboard.procurement.subtitle", "What needs your attention today")}</div>
        </div>
      </div>

      {error && <div className="tag err" role="alert">{error}</div>}
      {!data && !error && <p role="status">{t("common.loading", "Loading…")}</p>}

      <div style={kpiStrip}>
        {/* Purchases only, the same rule as the sales tile: the opening balance and the other
            postings are owed too, but summed as purchases they would compare a year that carries an
            opening balance with one that does not, so they are named under the value instead. */}
        {showFinance && data.purchasedYtd !== null && <KpiCard
          delay={0}
          label={t("dashboard.procurement.kpi.purchasedYtd", "Purchased year to date, incl. VAT")}
          value={data?.purchasedYtd ?? 0}
          format={novac}
          delta={data?.purchasedYtdDeltaPct ?? undefined}
          deltaLabel={data?.purchasedYtdDeltaPct == null
            ? t("dashboard.sales.kpi.noBase", "lane nije bilo prometa") : undefined}
          note={nijeUracunato || otkupDo
            ? <>
              {nijeUracunato && <div>{nijeUracunato}</div>}
              {otkupDo && <div>{otkupDo}</div>}
            </>
            : undefined}
          spark={mesecni.map((m) => m.value)}
          color="var(--brand-primary)"
        />}
        {/* Broj kamiona, a ispod njega masa koju su doneli: broj sam po sebi ne kaže koliko je
            robe ušlo, a masa sama ne kaže koliko je vožnji trebalo da uđe. */}
        {showIntake && data.intakesThisMonth !== null && <KpiCard
          delay={1}
          label={t("dashboard.procurement.kpi.intakesMonth", "Intakes this month")}
          value={data?.intakesThisMonth ?? 0}
          format={(v) => brojLokalno(v, locale, { maximumFractionDigits: 0 })}
          delta={data?.intakesDeltaPct ?? undefined}
          deltaLabel={data?.intakeKgThisMonth
            ? `${data?.intakesDeltaPct == null ? "" : " · "}${t("dashboard.procurement.kpi.intakeKg", "{{kg}} kg primljeno", {
              kg: brojLokalno(data.intakeKgThisMonth, locale, { maximumFractionDigits: 0 }),
            })}`
            : undefined}
          note={data.intakesUntil
            ? t("dashboard.procurement.kpi.dataUntil", "podaci do {{datum}}", { datum: fmtDate(data.intakesUntil) })
            : undefined}
          color="var(--tone-cyan)"
        />}
        {/* Ogledalo dospelog potraživanja sa prodajne strane, u drugom smeru: ovo je novac koji
            Bankom duguje. Pravilo je isto — iznos je zbir preko celog obima koji korisnik vidi, a
            ne zbir odsečenog spiska ispod. */}
        {showFinance && data.payableEur !== null && <KpiCard
          delay={2}
          label={t("dashboard.procurement.kpi.payable", "Dospela obaveza")}
          value={data?.payableEur ?? 0}
          format={novac}
          deltaLabel={data?.payableScopeLimited
            ? t("dashboard.procurement.kpi.payableMine", "vaših kooperanata sa dospelim: {{n}}",
              { n: brojLokalno(data?.payableCompanies ?? 0, locale, { maximumFractionDigits: 0 }) })
            : t("dashboard.procurement.kpi.payableAll", "kooperanata sa dospelim: {{n}}",
              { n: brojLokalno(data?.payableCompanies ?? 0, locale, { maximumFractionDigits: 0 }) })}
          deltaTone={(data?.payableEur ?? 0) > 0 ? "warn" : "ok"}
          note={data.dataUntil
            ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.dataUntil) })
            : undefined}
          color="var(--warn)"
        />}
      </div>

      {showIntake && <Section
        title={t("dashboard.procurement.queue.title", "Intakes awaiting your decision")}
        action={
          <Link to="/procurement/deliveries" style={actionLink}>
            {decisionQueue.length === 0
              ? t("dashboard.sales.viewAll", "View all")
              : t("dashboard.sales.review.viewAll", "View all ({{count}})", { count: decisionQueue.length })} →
          </Link>
        }
      >
        {waiting === null ? (
          <Muted>{t("common.loading", "Loading…")}</Muted>
        ) : decisionQueue.length === 0 ? (
          <Muted>
            {t("dashboard.procurement.queue.empty", "Nothing is waiting on you: no announcement needs confirming and the laboratory has stopped no load.")}
          </Muted>
        ) : (
          <ul style={reviewList}>
            {decisionQueue.map((d) => {
              const fresh = Date.now() - new Date(d.createdAt).getTime() < 24 * 3600 * 1000;
              const stopped = d.status === "LAB_REJECTED";
              return (
                <li key={d.id} style={reviewItem}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {fresh && (
                      <span
                        className="ord-fresh-dot"
                        aria-label={t("orders.row.freshLabel", "New")}
                        title={t("orders.row.freshTitle", "New — submitted in the last 24h")}
                      />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {d.companyName}
                      </div>
                      {/* Kamion se prepoznaje po tablicama, kao što se porudžbina prepoznaje po broju. */}
                      <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--mono)" }}>
                        {d.vehiclePlate ?? t("dashboard.procurement.queue.noPlate", "bez tablica")}
                        {d.estimatedQuantityT != null && ` · ${brojLokalno(d.estimatedQuantityT, locale, { maximumFractionDigits: 2 })} t`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={reviewStatusChip(stopped ? "review" : "submit")}>
                      {statusNaziv(d.status, t)}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                      {relTime(d.createdAt, locale, t)}
                    </span>
                    <Link to={`/procurement/deliveries/${d.id}`} className="btn sm" style={{ textDecoration: "none" }}>
                      {t("dashboard.sales.review.action", "Review")} →
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>}

      <div className="dash-two-col">
        {showFinance && <Section
          title={t("dashboard.procurement.charts.monthly", "Otkupljeno po mesecima")}
          action={
            <div style={{ display: "flex", gap: 4 }}>
              {[3, 6, 12].map((m) => (
                <button key={m} type="button" className="sd-tab-btn" aria-pressed={period === m}
                        onClick={() => setPeriod(m as 3 | 6 | 12)}>
                  {m}m
                </button>
              ))}
            </div>
          }
        >
          {mesecni.length === 0 ? (
            <Muted>{t("dashboard.procurement.charts.noInvoices", "Nema ulaznih računa u ovom periodu.")}</Muted>
          ) : (
            <BarChart data={mesecni.slice(-period)} color="var(--brand-primary)" formatValue={novac} />
          )}
        </Section>}

        {showIntake && <Section
          title={t("dashboard.procurement.charts.pipeline", "Prijemi po statusu")}
          action={pipelineFilter && (
            <button type="button" className="sd-tab-btn" onClick={() => setPipelineFilter(null)}>
              {t("dashboard.sales.charts.clearFilter", "Clear filter")}
            </button>
          )}
        >
          {tokPoFazi.length === 0 ? (
            <Muted>{t("dashboard.procurement.charts.noIntakes", "Nema prijema u ovoj godini.")}</Muted>
          ) : (
            <Donut
              slices={tokPoFazi}
              highlight={pipelineFilter}
              onSliceClick={(label) => setPipelineFilter(pipelineFilter === label ? null : label)}
              centerLabel={brojLokalno(
                pipelineFilter
                  ? (tokPoFazi.find((p) => p.label === pipelineFilter)?.value ?? 0)
                  : pipelineTotal,
                locale, { maximumFractionDigits: 0 })}
              centerSub={pipelineFilter ?? t("dashboard.procurement.charts.pipelineIntakes", "prijema")}
              formatValue={(v) => brojLokalno(v, locale, { maximumFractionDigits: 0 })}
            />
          )}
        </Section>}
      </div>

      {/* Pravilo: naslov obećava onoliko koliko tabela pokazuje. Tabela drži nekoliko najvećih
          poverilaca, pa i naslov kaže „najveće", a ispod stoji koliko je redova od koliko firmi,
          uz vezu na finansije dobavljača gde su sve. */}
      {showFinance && <Section
        title={t("dashboard.procurement.creditors.title", "Najveće obaveze")}
        action={
          <Link to="/procurement/finance" style={actionLink}>
            {t("dashboard.sales.viewAll", "Prikaži sve")} →
          </Link>
        }
      >
        {data === null ? (
          <Muted>{t("common.loading", "Učitavanje…")}</Muted>
        ) : data.topCreditors.length === 0 ? (
          <Muted>{t("dashboard.procurement.creditors.empty", "Nema dospelih obaveza prema kooperantima.")}</Muted>
        ) : (
          <>
            <LedgerTable
              rows={data.topCreditors}
              locale={locale}
              companyLabel={t("dashboard.procurement.creditors.company", "Kooperant")}
              hrefFor={(d) => `/procurement/finance/companies/${d.companyId}`}
            />
            {(data.payableCompanies ?? 0) > data.topCreditors.length && (
              <Muted>
                {t("dashboard.procurement.creditors.cut", "Prikazano {{prikazano}} od {{svih}} kooperanata.", {
                  prikazano: data.topCreditors.length,
                  svih: brojLokalno(data.payableCompanies ?? 0, locale, { maximumFractionDigits: 0 }),
                })}
              </Muted>
            )}
          </>
        )}
      </Section>}

      <div className="dash-two-col">
        {showFinance && <Section
          title={t("dashboard.procurement.charts.topSuppliers", "Najveći kooperanti (otkupljeno ove godine)")}
          /* Veza vodi u finansije dobavljača, a ceo odeljak se crta samo uz pravo na njih, pa
             drugog uslova nema: dugme koje vodi tamo gde se sme ući ne treba svoju dozvolu. */
          action={<Link to="/procurement/finance" style={actionLink}>{t("dashboard.sales.viewAll", "Prikaži sve")} →</Link>}
        >
          {(data?.topSuppliers.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.procurement.suppliers.empty", "Ove godine još nije otkupljeno ni od jednog kooperanta u vašem opsegu.")}</Muted>
          ) : (
            <HorizontalBars
              data={(data?.topSuppliers ?? []).map((c) => ({
                label: `${c.country ? flagEmoji(c.country) + " " : ""}${c.name}`, value: c.amountEur,
              }))}
              max={data?.topSuppliers[0]?.amountEur ?? 1}
              format={novac}
            />
          )}
        </Section>}

        {showFinance && <Section title={t("dashboard.procurement.charts.byCountry", "Otkupljeno po državama")}>
          {(data?.byCountry.length ?? 0) === 0 ? (
            <Muted>{t("dashboard.procurement.charts.byCountryEmpty", "Nema otkupa u ovoj godini.")}</Muted>
          ) : (
            <HorizontalBars
              data={countryBars(data?.byCountry ?? [], locale)}
              max={data?.byCountry[0]?.value ?? 1}
              format={novac}
            />
          )}
        </Section>}
      </div>

    </div>
  );
}

/**
 * Status prijema na jeziku korisnika.
 *
 * Isti prevodi koje koristi i spisak isporuka; kontrolna tabla ne sme da ih zove drugačije, jer
 * se sa nje klikom ide baš na taj spisak.
 */
function statusNaziv(status: string, t: TFunction): string {
  const kljuc: Record<string, [string, string]> = {
    ANNOUNCED: ["deliveries.status.announced", "Najavljeno"],
    CONFIRMED: ["deliveries.status.confirmed", "Potvrđeno"],
    SAMPLED: ["deliveries.status.sampled", "Uzorak uzet"],
    LAB_REJECTED: ["deliveries.status.labRejected", "Laboratorija odbila"],
    RECEIVED: ["deliveries.status.received", "Primljeno"],
    SETTLED: ["deliveries.status.settled", "Obračunato"],
    REJECTED: ["deliveries.status.rejected", "Odbijeno"],
    CANCELLED: ["deliveries.status.cancelled", "Otkazano"],
  };
  const par = kljuc[status];
  return par ? t(par[0], { defaultValue: par[1] }) : status;
}

/**
 * Boja prati put kamiona: najava hladna, uzorak topao, primljeno i obračunato zeleno, odbijeno
 * crveno. Isti red boja kao na prodajnoj strani, da ista faza znači isto na oba ekrana.
 *
 * `SAMPLED` je topao namerno — to je jedino stanje u kome kamion stoji i čeka laboratoriju.
 */
function statusBoja(status: string): string {
  switch (status) {
    case "ANNOUNCED": return "var(--info)";
    case "CONFIRMED": return "var(--tone-cyan)";
    case "SAMPLED": return "var(--warn)";
    case "RECEIVED": return "var(--brand-primary)";
    case "SETTLED": return "var(--ok)";
    case "LAB_REJECTED":
    case "REJECTED": return "var(--err)";
    case "CANCELLED": return "var(--ink-4)";
    default: return "var(--ink-3)";
  }
}
